import {
  autoGenerate,
  cancel,
  checkNumber,
  get,
  index,
  post,
  put,
  getFromPB,
} from "src/api/debit-note";

import DebitNoteForm from "src/modules/accounts/components/forms/DebitNoteForm.vue";
import DebitNoteRequestForm from "src/modules/purchase-order/components/forms/DebitNoteRequestForm.vue";
import { findIndex } from "src/assets/scripts/utilities/helpers";
import DebitNoteDetails from "src/modules/debit-notes/components/DebitNoteDetails.vue";

const getInitialState = () => {
  return {
    debitNotes: [],
    debitNote: {},
  };
};

const prepareDebitNote = (debitNote) => {
  const {
    bill_id,
    buyer,
    created_by,
    po_id,
    reference_invcs,
    seller,
    ...rest
  } = debitNote;

  debitNote = {
    ...rest,
    bill_id: bill_id?._id || bill_id,
    buyer: buyer?._id || buyer,
    created_by: created_by?._id || created_by,
    po_id: po_id?._id || po_id,
    reference_invcs: reference_invcs.map(({ _id, number } = {}) => {
      return { _id, number };
    }),
    seller: seller?._id || seller,
  };

  return debitNote;
};

export const useDebitNoteStore = defineStore("debit-note", () => {
  const formState = ref({
    edit: false,
    open: false,
    preview: false,
    view: false,
  });

  const toggleDebitNoteForm = (state = {}) => {
    formState.value = {
      edit: false,
      open: !formState.value.open,
      preview: false,
      view: false,

      ...state,
    };

    const { toggleRightDrawer, toggleNestedRightDrawer } = useAppStore();

    if (Object.hasOwn(unref(formState), "level")) {
      toggleNestedRightDrawer({
        component: DebitNoteForm,
        props: unref(formState).props,
        level: unref(formState).level,
        open: unref(formState).open,
      });
    } else {
      toggleRightDrawer(unref(formState).open, DebitNoteForm);
    }
  };

  const toggleDebitNoteRequestForm = (state = {}) => {
    formState.value = {
      edit: false,
      open: !formState.value.open,
      view: false,
      ...state,
    };

    const { toggleRightDrawer } = useAppStore();

    toggleRightDrawer(unref(formState).open, DebitNoteRequestForm);
  };

  const {
    state: debitNoteDetailsDrawerState,
    toggle: toggleDebitNoteDetailsDrawer,
  } = useDrawer({
    component: DebitNoteDetails,
  });

  let state = reactive(getInitialState());

  const hasGeneratedDebitNote = computed(() => {
    return state.debitNotes.some(
      (debitNote) => debitNote.stage_cd === "in_progress" || debitNote?.number,
    );
  });

  const addDebitNote = (debitNote) => {
    if (state.debitNotes) {
      state.debitNotes.unshift(debitNote);
    } else {
      state.debitNotes = [debitNote];
    }
  };

  const setDebitNote = (updatedDebitNote) => {
    state.debitNotes[findIndex(state.debitNotes, updatedDebitNote, "_id")] =
      updatedDebitNote;

    state.debitNote = updatedDebitNote;
  };

  const autoGenerateDebitNote = async (debitNote) => {
    try {
      const response = await autoGenerate(debitNote);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const cancelDebitNote = async (params) => {
    try {
      const response = await cancel(params);

      const { getCounts } = useAccountStore();

      await Promise.all([getCounts(), getDebitNotes()]);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getDebitNote = async ({ id, source = "kam" }) => {
    try {
      state.debitNote = state.debitNotes.find(
        (debitNote) => debitNote._id === id,
      );

      const response = await get(id, source);

      state.debitNote = response.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const filters = ref({
    po_id: null,
    stage: null,
  });

  const { filter, pagination, params } = usePagination(filters);

  const getDebitNotes = async (options = {}) => {
    try {
      options = unref(options);

      if (options?.filters) {
        filters.value = options?.filters;

        delete options.filters;
      }

      const response = await index({
        params: unref(params),
        ...options,
      });

      state.debitNotes = response.data?.data?.data;

      pagination.value.rowsNumber = response.data?.data?.count;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const getDebitNotesByPB = async (pbId) => {
    try {
      const response = await getFromPB(pbId);

      state.debitNotes = response.data?.data?.data;

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const postDebitNote = async (debitNote) => {
    try {
      const response = await post(prepareDebitNote(debitNote));

      const { getCounts } = usePurchaseOrderStore();

      await getCounts(response.data?.data?.po_id);

      addDebitNote(response.data?.data);

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const putDebitNote = async (debitNote) => {
    try {
      const response = await put(prepareDebitNote(debitNote));

      return response;
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const addFile = () => {
    getDebitNotes({
      filters: { ...filters, stage: "Not Started" },
    });
  };

  const updateFile = (file) => {
    const dnIndex = findIndex(state.debitNotes, { _id: file.dn_id }, "_id");

    const fileIndex = findIndex(
      state.debitNotes[dnIndex].files,
      { _id: file._id },
      "_id",
    );

    if (dnIndex !== -1 && fileIndex !== -1) {
      if (file.status === "Active") {
        state.debitNotes[dnIndex].files[fileIndex] = file;
      } else {
        state.debitNotes[dnIndex].files.splice(fileIndex, 1);
      }
    }
  };

  const isDuplicate = async (number) => {
    const response = await checkNumber(number);

    return response;
  };

  const reset = () => {
    Object.assign(state, getInitialState());
  };

  return {
    formState,
    debitNoteDetailsDrawerState,
    toggleDebitNoteDetailsDrawer,
    toggleDebitNoteForm,
    toggleDebitNoteRequestForm,

    filter,
    filters,
    pagination,

    ...toRefs(state),

    hasGeneratedDebitNote,

    setDebitNote,

    addFile,
    updateFile,

    getDebitNote,
    getDebitNotes,
    postDebitNote,
    putDebitNote,

    autoGenerateDebitNote,
    cancelDebitNote,
    getDebitNotesByPB,
    isDuplicate,

    reset,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useDebitNoteStore, import.meta.hot));
}
